import { NOTIFICATION_TYPES, type Notification } from '@warehouse/shared';
import { AlertTriangle, Bell, MessageSquareWarning, PackageCheck, Truck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { TFunction } from 'i18next';

/**
 * One place that turns a `notifications` row into a sentence and an icon, so
 * the bell dropdown and the notification centre page can never drift apart.
 *
 * Unknown types render a neutral fallback rather than a blank row: another
 * module can add a notification type without touching this file, and it still
 * shows up in the bell.
 */

const ICONS: Record<string, LucideIcon> = {
  [NOTIFICATION_TYPES.claimCreated]: MessageSquareWarning,
  [NOTIFICATION_TYPES.claimStatusChanged]: MessageSquareWarning,
  [NOTIFICATION_TYPES.claimResolved]: MessageSquareWarning,
  [NOTIFICATION_TYPES.receptionDamaged]: AlertTriangle,
  [NOTIFICATION_TYPES.orderConfirmed]: PackageCheck,
  [NOTIFICATION_TYPES.shipmentDispatched]: Truck,
};

const KNOWN_TYPES = new Set<string>(Object.values(NOTIFICATION_TYPES));

export function notificationIcon(notification: Notification): LucideIcon {
  return ICONS[notification.type] ?? Bell;
}

export function notificationText(notification: Notification, t: TFunction): string {
  const payload = (notification.payload ?? {}) as Record<string, unknown>;
  const params = {
    claimNo: str(payload.claimNo),
    restaurant: str(payload.restaurant),
    orderNo: str(payload.orderNo),
    status: str(payload.statusLabel) || str(payload.status),
  };
  const key = KNOWN_TYPES.has(notification.type) ? notification.type : 'unknown';
  return t(`claims:notifications.types.${key}`, params);
}

/** Where clicking the notification should take the user, if anywhere. */
export function notificationLink(notification: Notification, portal: boolean): string | null {
  const payload = (notification.payload ?? {}) as Record<string, unknown>;
  const claimId = str(payload.claimId);
  if (claimId) return portal ? `/portal/claims/${claimId}` : `/orders/claims/${claimId}`;
  const shipmentId = str(payload.shipmentId);
  if (shipmentId && portal) return `/portal/receive/${shipmentId}`;
  const orderId = str(payload.orderId);
  if (orderId) return portal ? `/portal/orders/${orderId}` : `/orders/sales-orders/${orderId}`;
  return null;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}
